import { useCallback, useState } from "react"
import {v4 as uuid4} from 'uuid'
import { useAppDispatch, useAppSelector } from "./reduxToolkit"
import { PendingSubmissionModel, SubmissionModel, UnsavedSubmissionModel, addSubmission, deleteSubmission, isPending, isUnsavedSubmissionModel, updateSubmission } from "../reduxToolkit/submissionsSlice"
import { IncidentModel } from "../reduxToolkit/incidentsSlice"
import { chooseSubmissionType, MinistryContext } from "../../lib/rules/chooseSubmissionType"
import { SubmissionType } from "../../types/supabase"
import { ExpenseModel, isCompleteExpense } from "../reduxToolkit/expensesSlice"
import { useCustomization } from "./useCustomizations"
import { Customizations } from "../reduxToolkit/selectors/customizations"
import { selectMinistryContext } from "../reduxToolkit/selectors/sharingMinistry"
import { getOrBuildPendingSubmissionModel, buildPendingSubmissionModel } from "../../lib/submissions/pendingSubmission"

export type PendingSubmissionUnion = PendingSubmissionModel | UnsavedSubmissionModel

/**
 * Builds a "pending" submission for the given incident, based on the submission type.
 * 
 * If the user had previously started a submission for this incident AND the submission type is still the same,
 * that PendingSubmissionModel will be returned.
 * 
 * Otherwise, a new UnsavedSubmissionModel will be initialized.
 * 
 * The createSubmission callback can be invoked to save the pending submission to the database.  This will assign it
 * an ID and allow us to navigate to the submission page.
 */
export function usePendingSubmission(incident: IncidentModel) {
  const dispatch = useAppDispatch()
  const hra = useCustomization('hra')
  const ministry = useAppSelector(selectMinistryContext)

  const allExpenses = useAppSelector((s) => s.expenses.expenses.filter((e) => e.incident_id === incident.id))
  const submissions = useAppSelector((s) => s.submissions.submissions.filter((e) => e.incident_id === incident.id))
  // Full membership submissions/incidents are needed to apply Sedera's annual
  // 3-incident IUA cap (see chooseSubmissionType).
  const allMembershipSubmissions = useAppSelector((s) => s.submissions.submissions)
  const allMembershipIncidents = useAppSelector((s) => s.incidents.incidents)

  const pendingSubmission = getOrBuildPendingSubmissionModel({
    incident,
    incidentSubmissions: submissions,
    incidentExpenses: allExpenses,
    allMembershipSubmissions,
    allMembershipIncidents,
  }, {
    hra,
    ministry,
  })
  const existingPendingSubmission = submissions.find(isPending)
  
  const [createdSubmissionId, setCreatedSubmissionId] = useState<string | null>(null)

  const createSubmission = useCallback((model: PendingSubmissionModel | UnsavedSubmissionModel): PendingSubmissionModel => {
    if (createdSubmissionId) {
      throw new Error("createSubmission was called more than once")
    }

    const now = new Date().toISOString()
    if (isUnsavedSubmissionModel(model)) {
      if (existingPendingSubmission) {
        // discard and restart the previous submission
        dispatch(deleteSubmission({
          id: existingPendingSubmission.id,
          updated_at: now,
          deleted_at: now
        }))
      }

      const id = uuid4()
      const newModel: PendingSubmissionModel = {
        ...model,
        id: id,
        created_at: now,
        updated_at: now
      }
      dispatch(addSubmission(newModel))
      setCreatedSubmissionId(id)
      return newModel
    } else {
      // We've already created this submission but we're re-creating the same one for some reason.
      // Just update it.
      const updatedModel = {
        ...model,
        updated_at: now
      }
      dispatch(updateSubmission(updatedModel))
      setCreatedSubmissionId(updatedModel.id)
      return updatedModel
    }
  }, [dispatch, existingPendingSubmission, createdSubmissionId])

  return [
    pendingSubmission,
    createSubmission
  ] as const
}

// Re-exported so existing `from '.../hooks/usePendingSubmission'` imports keep
// resolving. These moved to lib/submissions/pendingSubmission so that lib/todos
// can use them without a lib -> app/hooks dependency.
export { getOrBuildPendingSubmissionModel, buildPendingSubmissionModel }
